import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Wrench, Loader2, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import CommentThread from "@/components/CommentThread";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
  projectId: string;
  myRole?: "owner" | "editor" | "viewer";
  currentUserId?: string;
  requirements: { id: string; title: string }[];
}

const STATUS_COLORS: Record<string, string> = {
  open: "bg-yellow-100 text-yellow-800 border-yellow-300",
  "in-review": "bg-blue-100 text-blue-800 border-blue-300",
  closed: "bg-green-100 text-green-800 border-green-300",
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  open: <Clock className="h-3 w-3" />,
  "in-review": <AlertTriangle className="h-3 w-3" />,
  closed: <CheckCircle2 className="h-3 w-3" />,
};

const EMPTY_FORM = {
  nc_description: "",
  root_cause: "",
  corrective_action: "",
  preventive_action: "",
  owner: "",
  due_date: "",
  requirement_id: "",
};

export default function CAPAPanel({ baProjectId, projectId, myRole, currentUserId, requirements }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (baProjectId) load();
  }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/tools/capa?project_id=${baProjectId}`, { headers: authHeader });
      setRecords(data);
    } catch { setRecords([]); }
    finally { setLoading(false); }
  };

  const openNew = () => { setForm({ ...EMPTY_FORM }); setEditingId(null); setOpen(true); };
  const openEdit = (rec: any) => {
    setForm({
      nc_description: rec.nc_description || "",
      root_cause: rec.root_cause || "",
      corrective_action: rec.corrective_action || "",
      preventive_action: rec.preventive_action || "",
      owner: rec.owner || "",
      due_date: rec.due_date ? rec.due_date.slice(0, 10) : "",
      requirement_id: rec.requirement_id || "",
    });
    setEditingId(rec.id);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.nc_description.trim() || !baProjectId) return;
    setSaving(true);
    try {
      const body = {
        project_id: baProjectId,
        nc_description: form.nc_description,
        root_cause: form.root_cause || undefined,
        corrective_action: form.corrective_action || undefined,
        preventive_action: form.preventive_action || undefined,
        owner: form.owner || undefined,
        due_date: form.due_date || undefined,
        requirement_id: form.requirement_id || undefined,
      };
      if (editingId) {
        await axios.patch(`${BASE}/api/tools/capa/${editingId}`, body, { headers: authHeader });
      } else {
        await axios.post(`${BASE}/api/tools/capa`, body, { headers: authHeader });
      }
      setOpen(false);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleClose = async (id: string) => {
    await axios.patch(`${BASE}/api/tools/capa/${id}`, { status: "closed" }, { headers: authHeader });
    load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this CAPA record?")) return;
    await axios.delete(`${BASE}/api/tools/capa/${id}`, { headers: authHeader });
    load();
  };

  const setStatus = async (id: string, status: string) => {
    await axios.patch(`${BASE}/api/tools/capa/${id}`, { status }, { headers: authHeader });
    load();
  };

  const filtered = filterStatus === "all" ? records : records.filter(r => r.status === filterStatus);

  const reqTitle = (id: string) => requirements.find(r => r.id === id)?.title;

  if (!baProjectId) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        <Wrench className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
        Visit Requirements tab first to initialise the project backend.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Wrench className="h-4 w-4" /> CAPA Records
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Corrective &amp; Preventive Action — ISO 13485 §8.5</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="h-7 text-xs border border-input rounded-md px-2 bg-background"
          >
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="in-review">In Review</option>
            <option value="closed">Closed</option>
          </select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5" onClick={openNew}>
                <Plus className="h-3.5 w-3.5" /> New CAPA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">{editingId ? "Edit CAPA" : "New CAPA Record"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Nonconformance Description *</p>
                  <Textarea
                    placeholder="Describe what went wrong or what was not conforming"
                    value={form.nc_description}
                    onChange={e => setForm({ ...form, nc_description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Root Cause</p>
                  <Textarea placeholder="Why did this happen?" value={form.root_cause} onChange={e => setForm({ ...form, root_cause: e.target.value })} rows={2} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Corrective Action</p>
                  <Textarea placeholder="What will fix the immediate issue?" value={form.corrective_action} onChange={e => setForm({ ...form, corrective_action: e.target.value })} rows={2} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Preventive Action</p>
                  <Textarea placeholder="What will prevent recurrence?" value={form.preventive_action} onChange={e => setForm({ ...form, preventive_action: e.target.value })} rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Owner</p>
                    <Input placeholder="Responsible person" value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Due Date</p>
                    <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                  </div>
                </div>
                {requirements.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Linked Requirement (optional)</p>
                    <Select value={form.requirement_id} onValueChange={v => setForm({ ...form, requirement_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {requirements.map(r => <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button onClick={handleSave} disabled={!form.nc_description.trim() || saving} className="w-full gap-1.5">
                  {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : editingId ? "Save Changes" : "Create CAPA"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading && <p className="text-xs text-muted-foreground animate-pulse">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 border border-border rounded-md bg-secondary/10">
          <Wrench className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
          <p className="text-sm font-semibold">{records.length === 0 ? "No CAPA records yet" : "No records match filter"}</p>
          <p className="text-xs text-muted-foreground mt-1">{records.length === 0 ? "Create your first corrective action above." : "Try adjusting the status filter."}</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(rec => (
          <div key={rec.id} className="border border-border rounded-md bg-card p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-card-foreground flex-1">{rec.nc_description}</p>
              <div className="flex items-center gap-1.5 shrink-0">
                <Badge variant="outline" className={`text-[10px] px-1.5 flex items-center gap-1 ${STATUS_COLORS[rec.status] || ""}`}>
                  {STATUS_ICONS[rec.status]} {rec.status}
                </Badge>
              </div>
            </div>

            <div className="flex gap-3 text-[11px] text-muted-foreground font-mono flex-wrap">
              {rec.owner && <span>owner: {rec.owner}</span>}
              {rec.due_date && <span>due: {rec.due_date.slice(0, 10)}</span>}
              {rec.requirement_id && reqTitle(rec.requirement_id) && (
                <span>req: {reqTitle(rec.requirement_id)}</span>
              )}
              {rec.closed_at && <span>closed: {new Date(rec.closed_at).toLocaleDateString()}</span>}
            </div>

            {rec.root_cause && (
              <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Root cause:</span> {rec.root_cause}</p>
            )}
            {rec.corrective_action && (
              <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Corrective:</span> {rec.corrective_action}</p>
            )}
            {rec.preventive_action && (
              <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Preventive:</span> {rec.preventive_action}</p>
            )}

            <div className="flex gap-1.5 flex-wrap pt-1">
              {rec.status !== "closed" && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setStatus(rec.id, rec.status === "open" ? "in-review" : "open")}>
                  {rec.status === "open" ? "→ In Review" : "← Back to Open"}
                </Button>
              )}
              {rec.status !== "closed" && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleClose(rec.id)}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Close
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openEdit(rec)}>Edit</Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-destructive border-destructive/30 hover:bg-destructive/5" onClick={() => handleDelete(rec.id)}>Delete</Button>
            </div>

            <CommentThread
              projectId={projectId}
              entityType="capa"
              entityId={rec.id}
              currentUserId={currentUserId}
              myRole={myRole}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
